import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { observer } from 'mobx-react';
import { Box } from '@material-ui/core';
import { RangeInput } from '@material-ui/pickers';
import SystemUpdateAltOutlinedIcon from '@material-ui/icons/SystemUpdateAltOutlined';
import Routes from 'routes';
import { useReportContext } from 'contexts/report/ReportContext';
import reportLocales from 'globals/locales/report';
import DefaultLocales from 'globals/locales/default';
import { isSuccessState, wait } from 'globals/utils';
import { FsComponentSize, ModalState } from 'globals/types';
import FsBodyMediumBold from 'ui-elements/typography/FsBodyMediumBold';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import FsDateRangePicker from 'ui-elements/form-elements/FsDateRangePicker';
import FsErrorText from 'ui-elements/typography/FsErrorText';
import FsDialogTitle from 'ui-elements/modals/FsDialogTitle';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsBankLogoLoader from 'ui-elements/image-loaders/FsBankLogoLoader';
import {
  BankLabel,
  BankLabelWrapperBox,
  ContentWrapperBox,
  CustomDialog,
  DialogActions,
  DialogContent,
} from '../ReportModals.style-elements';
import SingleAccountExportSelector from './SingleAccountExportSelector';
import { getDefaultFallbackComponentStyle } from './ReportExportModal.style';
import { SingleAccountExportPayloadProps } from './SingleAccountExportSelector/SingleAccountExportSelector';
import { getNewAccountsPayload } from './SingleAccountExportSelector/SingleAccountExportSelector.utils';
import {
  getInitialDateRange,
  isProperDateRange,
} from './ReportExportModal.utils';

export interface FileListItemProps {
  name: string;
  url: string;
}

export interface ReportExportModalLocationStateProps {
  success?: ModalState;
  fileList?: FileListItemProps[];
}

const {
  selectDateRangeCaption,
  selectFileTypeCaption,
  errors,
} = reportLocales.exportModal;

const { export: exportLabel, download: downloadLabel } = DefaultLocales;

const ReportExportModal: React.FC<RouteComponentProps> = ({ history }) => {
  const {
    report: {
      bankAccountsList: { status, getSimpleBankList },
    },
  } = useReportContext();
  const banks = getSimpleBankList();
  const defaultFallbackComponentStyle = getDefaultFallbackComponentStyle();
  const [dateRange, setDateRange] = useState<RangeInput<Date>>(
    getInitialDateRange()
  );

  const [accountsPayload, setAccountsPayload] = useState<
    Array<SingleAccountExportPayloadProps>
  >([]);

  // conditions
  const [pending, setPending] = useState<boolean>(false);
  const [showAllErrors, setShowAllErrors] = useState<boolean>(false);
  const [isDateRangeError, setIsDateRangeError] = useState<boolean>(false);
  const isAccountPayloadEmpty = !accountsPayload.length;
  const isValidDateRange = isProperDateRange(dateRange);
  const isSendButtonDisabled =
    isDateRangeError ||
    (showAllErrors && (!isValidDateRange || isAccountPayloadEmpty));
  const isFormValid =
    isValidDateRange && !isAccountPayloadEmpty && !isDateRangeError;

  const handleClose = (): void => {
    history.push({ pathname: Routes.REPORT });
  };

  const redirectToModalSuccess = (): void => {
    const pathname = Routes.REPORT_MODAL_EXPORT_SUCCESS;
    const state: ReportExportModalLocationStateProps = {
      success: ModalState.SUCCESS,
      fileList: [
        {
          name: 'FileName 1',
          url:
            'https://www.learningcontainer.com/wp-content/uploads/2020/05/sample-large-zip-file.zip',
        },
        {
          name: 'FileName 2',
          url:
            'https://www.learningcontainer.com/wp-content/uploads/2020/05/sample-large-zip-file.zip',
        },
      ],
    };
    history.push({
      pathname,
      state,
    });
  };

  const redirectToModalFail = (): void => {
    const pathname = Routes.REPORT_MODAL_EXPORT_FAIL;
    const state: ReportExportModalLocationStateProps = {
      success: ModalState.FAIL,
    };
    history.push({
      pathname,
      state,
    });
  };

  const handleSend = (): boolean => {
    // TODO: API | bind with proper endpoint
    const dateRangeTimestamps = dateRange.map((date) =>
      (date as Date).getTime()
    );

    const payload = { dateRange: dateRangeTimestamps, accountsPayload };
    // eslint-disable-next-line no-console
    console.log('payload', JSON.stringify(payload, null, 2));
    return Math.random() < 0.5;
  };

  const handleSendAndClose = async (): Promise<void> => {
    setShowAllErrors(true);
    if (isFormValid) {
      const sendStatus = handleSend();
      setPending(true);
      await wait(300);
      setPending(false);
      handleClose();
      if (sendStatus) {
        redirectToModalSuccess();
        return;
      }
      redirectToModalFail();
    }
  };

  const changeDateRangeHandler = (range: RangeInput<Date>): void => {
    setDateRange(range);
  };

  const errorDateRangeHandler = (errorStatus: boolean): void => {
    setIsDateRangeError(errorStatus);
  };

  const changeAccountListItemHandler = (
    newSingleAccountPayload: SingleAccountExportPayloadProps
  ): void => {
    const newPayload = getNewAccountsPayload(
      accountsPayload,
      newSingleAccountPayload
    );
    setAccountsPayload(newPayload);
  };

  return (
    <CustomDialog
      disableEnforceFocus
      open
      fullWidth={false}
      maxWidth="md"
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      data-test="report-export-modal"
    >
      <FsDialogTitle id="report-export-modal" onClose={handleClose}>
        {exportLabel}
      </FsDialogTitle>

      <DialogContent dividers>
        <ContentWrapperBox>
          <Box marginBottom={2}>
            <FsBodyMediumBold>{selectDateRangeCaption}</FsBodyMediumBold>
            {showAllErrors && !isValidDateRange && (
              <FsErrorText>
                {errors.dateRangeInvalidOrPartiallyNull}
              </FsErrorText>
            )}
            {isDateRangeError && (
              <FsErrorText>{errors.dateRangeInvalid}</FsErrorText>
            )}
          </Box>

          <Box marginBottom={4}>
            <FsDateRangePicker
              dateRange={dateRange}
              onChange={changeDateRangeHandler}
              onError={errorDateRangeHandler}
              data-test="date-range-picker"
            />
          </Box>

          <Box marginBottom={1}>
            <FsBodyMediumBold>{selectFileTypeCaption}</FsBodyMediumBold>
            {showAllErrors && isAccountPayloadEmpty && (
              <FsErrorText data-test="account-warning-message">
                {errors.accountPayloadEmpty}
              </FsErrorText>
            )}
          </Box>
          <Box>
            {isSuccessState(status) ? (
              banks.map(({ name: bankName, id: bankId, accounts }) => {
                const accountItems = accounts.map(({ id, name, currency }) => (
                  <SingleAccountExportSelector
                    key={id}
                    id={id}
                    name={name}
                    currency={currency}
                    onChange={changeAccountListItemHandler}
                    data-test="single-account-export-selector"
                  />
                ));
                return [
                  <BankLabelWrapperBox key={bankId} data-test="bank-label">
                    <FsBankLogoLoader
                      bankName={bankName}
                      size={FsComponentSize.S}
                    />
                    <BankLabel>{bankName}</BankLabel>
                  </BankLabelWrapperBox>,
                  ...accountItems,
                ];
              })
            ) : (
              <FsDefaultFallbackComponent
                status={status}
                style={defaultFallbackComponentStyle}
                data-test="default-fallback-component"
              />
            )}
          </Box>
        </ContentWrapperBox>
      </DialogContent>
      <DialogActions>
        <FsPendingButton
          disabled={isSendButtonDisabled}
          pending={pending}
          data-test="download-button"
          onClick={handleSendAndClose}
          startIcon={<SystemUpdateAltOutlinedIcon />}
        >
          {downloadLabel}
        </FsPendingButton>
      </DialogActions>
    </CustomDialog>
  );
};

export default observer(ReportExportModal);
